import { Directive, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import lodash from 'lodash';
import { Subscription, of } from 'rxjs';
import { distinct, filter, flatMap, map, toArray } from 'rxjs/operators';

import moment from 'moment';

import { gcManagementEnumerations } from '@interfaces/interfaces.module';

import { ClassReportModel, ContextModel, InstructorModel } from '@bayclubs/group-classes-employee-api';
import { ClassRow, ClubCentricClass, ReportModel, ReportType } from '../../models/reports-model';
import { ReportSelectService } from '../../services/report-select.service';
import { ReportsService } from '../../services/reports.service';

@Directive({
    standalone: false
})
export abstract class BaseReportComponent implements OnInit, OnDestroy {
    protected subscriptions: Subscription;
    public reportModel: ReportModel;
    protected reportType: ReportType;
    public generateDisabled: boolean;
    public context: ContextModel;
    public displayedClasses: ClubCentricClass[];
    public instructors: InstructorModel[];

    constructor(
        protected activatedRoute: ActivatedRoute,
        protected reportSelectService: ReportSelectService,
        protected reportsService: ReportsService,
        protected translateService: TranslateService
    ) {}
    ngOnInit() {
        this.reportModel = this.reportSelectService.getReport(this.reportType);
        this.context = lodash.cloneDeep(this.activatedRoute.snapshot.data.context);
        if (
            this.reportModel.type === ReportType.PayrollReport ||
            this.reportModel.type === ReportType.InstructorCentricReport
        ) {
            this.instructors = this.context.instructors.sort((a, b) => a.name.localeCompare(b.name));
        }
        this.generateDisabled = true;
        this.subscriptions = new Subscription();
    }

    ngOnDestroy() {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }

    protected downloadReportFile(value: any, fileName: string) {
        const url = URL.createObjectURL(value);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const reportName = fileName + this.getLocalDate(Date.now(), 'YYYYMMDD_hhmmA') + '.xlsx';
        link.setAttribute('download', reportName);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    public getLocalDate(inputDate: any, format: string): string {
        const utcDate = moment.utc(inputDate);
        const localDate = utcDate.local().format(format);
        return localDate;
    }

    protected calculateSummaryRowTotalHours(classes: ClassRow[]): number {
        return (
            classes.reduce((p, a) => {
                p += a.billableLengthOfClass;
                return p;
            }, 0) / 60
        );
    }

    protected calculateSummaryRowTotalCost(classes: ClassRow[]): number {
        return classes.reduce((p, a) => {
            p += a.cost;
            return p;
        }, 0);
    }

    protected calculateStartDate(date: Date, classTimeFromInMinutes: number): Date {
        return moment(date)
            .add(classTimeFromInMinutes, 'm')
            .toDate();
    }

    protected fillClassItem(groupClass: ClassReportModel, classItem: ClassRow) {
        const club = this.context.clubs.find(c => c.id === groupClass.clubId);
        if (club) {
            classItem.clubName = club.name;
            classItem.clubShortName = club.shortName;
        }

        classItem.className = groupClass.className;
        classItem.billableLengthOfClass = groupClass.classBillableTimeInMinutes;
        classItem.actualLengthOfClass = groupClass.classTimeToInMinutes - groupClass.classTimeFromInMinutes;
        classItem.classAttendance = groupClass.classAttendance;
        classItem.cost = groupClass.cost;
        classItem.location = groupClass.locationName;
        classItem.bcDate = groupClass.classDate;
        classItem.date = new Date(groupClass.classDate.value);
        classItem.startTime = groupClass.classTimeFromInMinutes;
        classItem.status = groupClass.classStatus;
        classItem.cancelled = classItem.status.code === gcManagementEnumerations.GroupClassStatus.cancelled.code;
        classItem.substitution = groupClass.instructorIsSubstitute;
    }

    protected loadSelectedClasses(groupClasses: ClassReportModel[], instructors: string[]) {
        this.displayedClasses = [];
        this.displayedClasses.push(
            new ClubCentricClass(null, this.translateService.instant('ReportsCriteria.SelectClassNamePlaceholder'))
        );
        this.displayedClasses = [
            ...this.displayedClasses,
            ...groupClasses
                .filter(p => {
                    if (instructors) {
                        return instructors.includes(p.instructorId);
                    } else {
                        return true;
                    }
                })
                .map(item => item.className)
                .filter((value, index, self) => self.indexOf(value) === index)
                .map(className => new ClubCentricClass(className, className))
                .sort((a, b) => a.name.localeCompare(b.name)),
        ];
    }

    protected loadSelectedInstructors(groupClasses: ClassReportModel[]) {
        const instructorsDictionary: { [id: string]: InstructorModel } = this.context.instructors.reduce(
            (acc, x: InstructorModel) => {
                acc[x.id] = x;
                return acc;
            },
            {}
        );

        of(groupClasses)
            .pipe(
                flatMap(classes => classes),
                map(c => c.instructorId),
                distinct(),
                map(x => instructorsDictionary[x]),
                filter(x => x != null),
                toArray()
            )
            .subscribe(x => (this.instructors = x.sort((a, b) => a.name.localeCompare(b.name))));
    }

    // Code from stackOverflow https://stackoverflow.com/questions/6913512/how-to-sort-an-array-of-objects-by-multiple-fields/42767569#42767569
    public sort(data, orderBy) {
        orderBy = Array.isArray(orderBy) ? orderBy : [orderBy];
        return data.sort((a, b) => {
            for (let i = 0, size = orderBy.length; i < size; i++) {
                const key = Object.keys(orderBy[i])[0],
                    o = orderBy[i][key],
                    valueA = a[key],
                    valueB = b[key];
                if (!(valueA || valueB)) {
                    return [];
                }
                if (+valueA === +valueA) {
                    return o.toLowerCase() === 'desc' ? valueB - valueA : valueA - valueB;
                } else {
                    if (valueA.localeCompare(valueB) > 0) {
                        return o.toLowerCase() === 'desc' ? -1 : 1;
                    } else if (valueA.localeCompare(valueB) < 0) {
                        return o.toLowerCase() === 'desc' ? 1 : -1;
                    }
                }
            }
        });
    }
}
